"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Apple, Monitor, Smartphone, Globe, Cloud, Laptop, Tablet, CirclePlay } from "lucide-react";
const platforms = [{
  id: "ios-mac",
  name: "Apple 生态",
  icon: Apple,
  position: "top-[10%] left-[15%] md:top-[20%] md:left-[20%]",
  color: "text-foreground",
  delay: "delay-100"
}, {
  id: "windows",
  name: "Windows",
  icon: Monitor,
  position: "top-[15%] right-[15%] md:top-[25%] md:right-[20%]",
  color: "text-secondary",
  delay: "delay-200"
}, {
  id: "android",
  name: "Android",
  icon: Smartphone,
  position: "bottom-[15%] left-[20%] md:bottom-[25%] md:left-[25%]",
  color: "text-primary",
  delay: "delay-300"
}, {
  id: "web",
  name: "Web 网页端",
  icon: Globe,
  position: "bottom-[10%] right-[15%] md:bottom-[20%] md:right-[25%]",
  color: "text-chart-5",
  delay: "delay-400"
}];
export default function AnytimeAnywhereSeamlessSync() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: isVisualVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="随时随地无缝同步" className="relative overflow-hidden bg-background py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -ml-[40rem] -mt-[20rem] h-[40rem] w-[80rem] rounded-full bg-primary/5 blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -ml-[20rem] -mt-[10rem] h-[30rem] w-[60rem] rounded-full bg-secondary/5 blur-[80px]" />
        <Cloud className="absolute top-20 left-20 h-32 w-32 text-muted/30" />
        <Cloud className="absolute bottom-20 right-20 h-48 w-48 text-muted/30" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div ref={headerRef} className={`mx-auto max-w-2xl text-center transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            随时随地，无缝同步
          </h2>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            学习进度永不丢失，通勤、午休、睡前，想练就练。无论使用哪种设备，你的听力训练始终保持最新状态。
          </p>
        </div>

        {}
        <div ref={visualRef} className={`relative mt-16 flex h-[500px] w-full items-center justify-center transition-all duration-1000 ease-out sm:mt-24 ${isVisualVisible ? "translate-y-0 opacity-100 scale-100" : "translate-y-12 opacity-0 scale-95"}`}>
          {}
          {platforms.map((platform, i) => <div key={platform.id} data-index={i} className={`absolute z-20 flex flex-col items-center gap-2 transition-all duration-700 ${platform.position} ${isVisualVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${platform.delay}`}>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-card shadow-xl ring-1 ring-border/50">
                <platform.icon className={`h-7 w-7 ${platform.color}`} />
              </div>
              <span className="hidden rounded-full bg-background/80 px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur-sm md:block">
                {platform.name}
              </span>
            </div>)}

          {}
          <div className="relative z-10 flex items-center justify-center">
            
            {}
            <Card className="absolute -top-12 left-1/2 h-64 w-[320px] -translate-x-1/2 transform rounded-t-3xl border-border/40 bg-card/95 p-6 shadow-2xl backdrop-blur-md sm:w-[480px] sm:h-72">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Laptop className="h-5 w-5 text-primary" />
                  <div className="h-2 w-24 rounded-full bg-muted" />
                </div>
                <div className="flex gap-1">
                  <div className="h-2 w-2 rounded-full bg-destructive/50" />
                  <div className="h-2 w-2 rounded-full bg-accent" />
                  <div className="h-2 w-2 rounded-full bg-primary/50" />
                </div>
              </div>
              <div className="mt-8 space-y-6">
                <div className="flex items-center gap-4">
                  <CirclePlay className="h-10 w-10 text-primary" />
                  <div className="space-y-2 flex-1">
                    <div className="h-3 w-3/4 rounded-full bg-muted" />
                    <div className="h-2 w-1/2 rounded-full bg-muted/50" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm font-medium text-muted-foreground">
                    <span>今日精听进度</span>
                    <span className="text-primary">75%</span>
                  </div>
                  <Progress value={75} className="h-3" />
                </div>
              </div>
            </Card>

            {}
            <Card className="absolute -left-16 top-12 z-20 h-56 w-48 -rotate-6 transform rounded-2xl border-border/50 bg-card p-4 shadow-xl sm:-left-32 sm:h-64 sm:w-56">
              <div className="mb-4 flex justify-center">
                <div className="h-1 w-12 rounded-full bg-muted" />
              </div>
              <div className="flex h-full flex-col justify-between pb-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Tablet className="h-4 w-4 text-secondary" />
                    <div className="h-2 w-16 rounded-full bg-muted" />
                  </div>
                  <div className="h-16 w-full rounded-xl bg-muted/30" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-medium text-muted-foreground">
                    <span>同步进度</span>
                    <span className="text-primary">75%</span>
                  </div>
                  <Progress value={75} className="h-2" />
                </div>
              </div>
            </Card>

            {}
            <Card className="absolute -right-12 top-20 z-30 h-60 w-32 rotate-6 transform rounded-[2rem] border-4 border-border/20 bg-card p-3 shadow-2xl sm:-right-24 sm:h-72 sm:w-40">
              <div className="mb-2 flex justify-center">
                <div className="h-1 w-10 rounded-full bg-muted" />
              </div>
              <div className="flex h-full flex-col justify-end pb-2">
                <div className="mb-auto mt-2 space-y-2">
                  <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                     <Smartphone className="h-4 w-4 text-primary" />
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted" />
                  <div className="h-2 w-2/3 rounded-full bg-muted" />
                </div>
                <div className="space-y-1.5 rounded-xl bg-muted/20 p-2">
                  <div className="flex justify-between text-[10px] font-medium text-muted-foreground">
                    <span>进度</span>
                    <span className="text-primary">75%</span>
                  </div>
                  <Progress value={75} className="h-1.5" />
                </div>
              </div>
            </Card>
            
          </div>
        </div>

        {}
        <div className="mt-16 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
          <Button asChild size="lg" className="min-w-[160px] rounded-full text-base h-12">
            <Link to="/DownloadCenterPage">免费下载</Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="min-w-[160px] rounded-full text-base h-12 border-primary/20 hover:bg-primary/5">
            <Link to="/OnlineAppPage">在线体验</Link>
          </Button>
        </div>
      </div>
    </section>;
}